import React, { createContext, useContext, useState, useEffect } from 'react';
import { EndUser, EndUserLogin, EndUserRegister, EndUserLoginResponse } from '../types/endUser';
import customerApiService, { CUSTOMER_IMPERSONATION_KEY } from '../services/customerApi';
import { decodeJwtClaims, isImpersonationClaims } from '../utils/jwt';
import { onPasswordChangeRequired, portalLoginErrorFromFetch } from '../utils/portalAuthErrors';
import { loginWithGoogle as googleLoginFlow } from '../utils/portalGoogleLogin';

interface CustomerAuthContextType {
  user: EndUser | null;
  isAuthenticated: boolean;
  // Modo impersonación (partner «viendo como cliente»): TODO es solo lectura.
  // Se detecta por el token por-pestaña de sessionStorage o por claim del JWT.
  readOnly: boolean;
  // Contrato F1: entró con la contraseña de un solo uso y aún no ha creado la
  // suya. CustomerProtectedRoute pinta el gate bloqueante mientras sea true.
  mustChangePassword: boolean;
  login: (credentials: EndUserLogin) => Promise<boolean>;
  // «Entrar con Google»: misma sesión y mismo estado que el login por
  // contraseña — lo único distinto es cómo se demuestra la identidad.
  loginWithGoogle: () => Promise<boolean>;
  register: (data: EndUserRegister) => Promise<boolean>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

const CustomerAuthContext = createContext<CustomerAuthContextType | undefined>(undefined);

// EndUser mínimo construido desde los claims cuando /auth/me no está disponible
// para tokens de impersonación (backend en paralelo): la UI no rompe.
const stubUserFromClaims = (claims: Record<string, unknown> | null): EndUser => ({
  id: Number(claims?.sub) || 0,
  email: typeof claims?.email === 'string' ? claims.email : '',
  full_name:
    (typeof claims?.name === 'string' && claims.name) ||
    (typeof claims?.full_name === 'string' && claims.full_name) ||
    'Cliente',
  phone: null,
  company: null,
  is_active: true,
  is_verified: true,
  created_at: '',
  updated_at: ''
});

export const CustomerAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<EndUser | null>(null);
  const [readOnly, setReadOnly] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load user from localStorage on mount
  useEffect(() => {
    // 1) Token de impersonación por-pestaña (prioridad): sesión solo-lectura.
    const impersonationToken = sessionStorage.getItem(CUSTOMER_IMPERSONATION_KEY);
    if (impersonationToken) {
      customerApiService.setImpersonationToken(impersonationToken);
      setReadOnly(true);
      const claims = decodeJwtClaims(impersonationToken);
      customerApiService
        .getMeTolerant()
        .then((me) => setUser(me ?? stubUserFromClaims(claims)))
        .catch(() => setUser(stubUserFromClaims(claims)))
        .finally(() => setLoading(false));
      return;
    }

    // 2) Sesión normal de localStorage.
    const token = localStorage.getItem('customer_auth_token');
    const storedUser = localStorage.getItem('customer_user');

    if (token && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        // El flag sobrevive a la recarga porque el perfil cacheado lo trae del
        // login: sin esto, un F5 saltaría el gate y dejaría la temporal viva.
        setMustChangePassword(!!parsedUser.must_change_password);
        // CRITICAL: Restore token to API service on mount
        customerApiService.setAuthToken(token);
        // El backend puede emitir tokens con claim de impersonación aunque se
        // hayan guardado por el flujo normal: respetar el solo-lectura.
        if (isImpersonationClaims(decodeJwtClaims(token))) {
          setReadOnly(true);
        }
        console.log('Customer auth restored from localStorage:', parsedUser.email);
      } catch (e) {
        console.error('Failed to parse stored user:', e);
        localStorage.removeItem('customer_auth_token');
        localStorage.removeItem('customer_user');
        customerApiService.clearAuth();
      }
    }
    setLoading(false);
  }, []);

  // Red de seguridad del veto en SERVIDOR: si el backend rechaza con 403
  // PASSWORD_CHANGE_REQUIRED (cache local desincronizada, otra pestaña, un
  // reset hecho por el admin mientras la sesión estaba abierta), se levanta el
  // gate SIN cerrar sesión. El perfil cacheado también se marca para que un F5
  // no vuelva a colar al usuario dentro del portal.
  useEffect(
    () =>
      onPasswordChangeRequired('customer', () => {
        setMustChangePassword(true);
        setUser((prev) => {
          if (!prev) return prev;
          const updated = { ...prev, must_change_password: true };
          localStorage.setItem('customer_user', JSON.stringify(updated));
          return updated;
        });
      }),
    []
  );

  const login = async (credentials: EndUserLogin): Promise<boolean> => {
    try {
      const response = await fetch('/api/v1/customer/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        // Contrato F1: el código funcional y los contadores viajan en cabeceras
        // (`X-Auth-Error-Code`, intentos restantes, minutos de bloqueo). El
        // error resultante ya trae el mensaje traducido para el Alert del login.
        throw await portalLoginErrorFromFetch(response);
      }

      const data: EndUserLoginResponse = await response.json();

      // Store token and user
      localStorage.setItem('customer_auth_token', data.access_token);
      localStorage.setItem('customer_user', JSON.stringify(data.user));
      setUser(data.user);
      setReadOnly(false);
      setMustChangePassword(!!(data.must_change_password ?? data.user?.must_change_password));

      // CRITICAL: Update the API service with the new token
      customerApiService.setAuthToken(data.access_token);

      return true;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  /**
   * Login con Google (SSO vía Composio/auth-service). La sesión que queda es
   * EXACTAMENTE la misma que la del login por contraseña —mismo token, mismo
   * `customer_user` en localStorage, mismo flag de contraseña pendiente— porque
   * el backend emite el mismo token: solo cambia cómo se identificó al usuario.
   */
  const loginWithGoogle = async (): Promise<boolean> => {
    const data = await googleLoginFlow<EndUserLoginResponse>('customer');
    localStorage.setItem('customer_auth_token', data.access_token);
    localStorage.setItem('customer_user', JSON.stringify(data.user));
    setUser(data.user);
    setReadOnly(false);
    // Un usuario mixto (contraseña + Google) que aún arrastre la temporal sigue
    // viendo el gate aunque haya entrado por Google: esa contraseña sigue viva.
    setMustChangePassword(!!(data.must_change_password ?? data.user?.must_change_password));
    customerApiService.setAuthToken(data.access_token);
    return true;
  };

  /** Salida del gate obligatorio: POST /customer/auth/change-password. */
  const changePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
    await customerApiService.changePassword({
      current_password: currentPassword,
      new_password: newPassword,
    });
    setMustChangePassword(false);
    // El perfil cacheado también deja de pedir el cambio: si no, una recarga
    // volvería a levantar el gate con la contraseña ya cambiada.
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, must_change_password: false };
      localStorage.setItem('customer_user', JSON.stringify(updated));
      return updated;
    });
  };

  const register = async (data: EndUserRegister): Promise<boolean> => {
    try {
      const response = await fetch('/api/v1/customer/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Registration failed');
      }

      const responseData: EndUserLoginResponse = await response.json();

      // Store token and user
      localStorage.setItem('customer_auth_token', responseData.access_token);
      localStorage.setItem('customer_user', JSON.stringify(responseData.user));
      setUser(responseData.user);
      setReadOnly(false);
      // Alta pública: la contraseña la ha elegido el propio usuario, así que
      // nunca hay cambio forzado (solo lo hay cuando la genera un tercero).
      setMustChangePassword(false);

      // CRITICAL: Update the API service with the new token
      customerApiService.setAuthToken(responseData.access_token);

      return true;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('customer_auth_token');
    localStorage.removeItem('customer_user');
    setUser(null);
    setReadOnly(false);
    setMustChangePassword(false);
    // Clear token from API service (también limpia la impersonación por-pestaña)
    customerApiService.clearAuth();
  };

  return (
    <CustomerAuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        readOnly,
        // En impersonación NUNCA se pide el cambio: el partner mira en solo
        // lectura y no es dueño de esa contraseña.
        mustChangePassword: mustChangePassword && !readOnly,
        login,
        loginWithGoogle,
        register,
        changePassword,
        logout,
        loading,
      }}
    >
      {children}
    </CustomerAuthContext.Provider>
  );
};

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (context === undefined) {
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider');
  }
  return context;
};
